import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { getAccessToken } from "./tokenUtils"; // Assuming tokenUtils handles storage
import apiEndpointManager from "../config"; // Import the API endpoint manager

// Create an Axios instance with initial configuration
// We'll update the baseURL after the endpoint manager is initialized
const apiClient: AxiosInstance = axios.create({
    headers: {
        "Content-Type": "application/json",
    },
});

// Set the base URL immediately with current value, but it might not be initialized yet
apiClient.defaults.baseURL = apiEndpointManager.endpoint;
console.log(`[apiClient] Initial baseURL set to: ${apiClient.defaults.baseURL}`);

// Also update baseURL once the endpoint manager is fully initialized
apiEndpointManager
    .getEndpoint()
    .then((endpoint) => {
        console.log(`[apiClient] API endpoint initialized to: ${endpoint}`);
        apiClient.defaults.baseURL = endpoint;
        console.log(`[apiClient] Updated baseURL: ${apiClient.defaults.baseURL}`);
    })
    .catch((error) => {
        console.error("[apiClient] Failed to initialize API endpoint:", error);
    });

// --- Axios Request Interceptor ---
// This function runs before each request is sent.
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // Remove duplicate /api prefix if it exists
        // This is important because the baseURL already contains /api
        if (config.url?.startsWith("/api/")) {
            config.url = config.url.replace("/api/", "/");
            console.log(`[apiClient] Removed duplicate /api prefix from URL path`);
        }

        // Log the complete URL being requested to debug path issues
        const fullUrl =
            config.baseURL + (config.url?.startsWith("/") ? config.url : "/" + config.url);
        // console.log(`[apiClient] Full request URL: ${fullUrl}`);

        // Get access token from storage
        const token = getAccessToken();

        // Debug: log token info without revealing the actual token
        if (token) {
            // // console.log("[apiClient] Request with token info:", {
            //     hasToken: true,
            //     tokenLength: token.length,
            //     tokenFirstChars: token.substring(0, 5) + "..." + token.substring(token.length - 5),
            //     endpoint: config.url,
            // });

            // AWS API Gateway with Cognito authorizer expects Bearer format
            config.headers.Authorization = `Bearer ${token}`;

            // Log the actual header being sent (first 10 chars only for security)
            const authHeader = config.headers.Authorization as string;
            // console.log(
            //     "[apiClient] Using Authorization header format:",
            //     authHeader.substring(0, 15) +
            //         "..." +
            //         (authHeader.length > 30 ? authHeader.substring(authHeader.length - 10) : ""),
            // );
        } else {
            // console.log("[apiClient] Request without token to:", config.url);
        }

        return config;
    },
    (error) => {
        console.error("[apiClient] Request Interceptor Error:", error);
        return Promise.reject(error);
    },
);

// --- Axios Response Interceptor --- (Optional but Recommended)
// This function runs after a response is received.
apiClient.interceptors.response.use(
    (response) => {
        // Any status code within the range of 2xx causes this function to trigger
        // You can process successful responses here if needed
        // // console.log("[apiClient] Response Interceptor Success:", response.status);
        return response;
    },
    (error) => {
        // Any status codes outside the range of 2xx cause this function to trigger
        // Handle global response errors (e.g., 401 Unauthorized, 403 Forbidden, 500 Server Error)
        console.error(
            "[apiClient] Response Interceptor Error:",
            error.response?.status,
            error.message,
        );

        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            const status = error.response.status;
            if (status === 401) {
                // Handle Unauthorized: e.g., redirect to login, clear token, dispatch logout action
                console.error("[apiClient] Unauthorized (401). Token might be invalid or expired.");
                // Example: window.location.href = '/login'; // Or dispatch a logout action
            } else if (status === 403) {
                console.error("[apiClient] Forbidden (403). User does not have permission.");
                // Potentially show a notification to the user
            } else {
                // Handle other server errors (4xx, 5xx)
                console.error(`[apiClient] Server responded with error status: ${status}`);
            }
        } else if (error.request) {
            // The request was made but no response was received (e.g., network error, CORS issue)
            console.error("[apiClient] Network error or no response received:", error.request);
        } else {
            // Something happened in setting up the request that triggered an Error
            console.error("[apiClient] Error setting up request:", error.message);
        }

        // Return a rejected promise to propagate the error to the calling code (e.g., async thunk)
        return Promise.reject(error);
    },
);

export default apiClient;
